//dont add
import React from 'react'
import clsx from 'clsx'
import { useAppSelector } from '@SonarCX/hooks/redux'
import { ArrowLeft, Happyemoji } from 'iconsax-react'

import EmojiSelect from '../../ChatWindow/Controls/TextArea/EmojiSelect'
import BaseModal from '../../../molecules/ChatModals/BaseModal2'
import DropdownV2 from '../../../atoms/DropdownV2'
import EmailInput from './Email'
import SmsInput from './Sms'
import UploadFile from '../../UploadFile'
import AttachmentIcon from '../../ChatWindowV2/ChatBox/InputControls/Icons/AttachmentIcon'
import MicrophoneIcon from '../../ChatWindowV2/ChatBox/InputControls/Icons/MicrophoneIcon'
import ThunderIcon from '../../ChatWindowV2/ChatBox/InputControls/Icons/ThunderIcon'
import SendIcon from '../../ChatWindowV2/ChatBox/InputControls/Icons/SendIcon'
import Attachments from '../../ChatWindowV2/ChatBox/Attachments'
import useSize from '@SonarCX/hooks/useSize'
import MentionInput from '@SonarCX/components/molecules/Mention'

import { useAppDispatch } from '@SonarCX/hooks/redux'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { transformToEmoji } from '@SonarCX/utilities/stringToEmoji'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { createSessionObjectV2 } from '../../Conversations/helpers'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { selectConvo, setTopFilter, TOP_FILTERS } from '@SonarCX/slices/convo'
import { createSession, getSession } from '@SonarCX/lib/api'
import { setConversation } from '@SonarCX/slices/convo'
import { useQueueState } from '@SonarCX/providers/QueueStateProvider'
import { useSocket } from '@SonarCX/providers/SocketProvider'

import {
  changeType,
  setChannelValue,
  switchSession,
  TYPES
} from '@SonarCX/slices/chatBoxSlice'

import useStyles from './styles'
import Button from '@SonarCX/components/atoms/Button2'

export type ChannelOptionsType = 'Email' | 'SMS'
const CHANNEL_OPTIONS: Array<ChannelOptionsType> = ['Email', 'SMS']
const UNDO_KEYCODE = 90
const SPACE_KEYCODE = 32
const MAX_SMS_TEXT_LENGHT = 300

const NewConversationModal = ({ onClose }) => {
  const dispatch = useAppDispatch()
  const {
    api: { formatConversation, messageHandlerV2 }
  } = useSocket()
  const { currentUser, joinSession } = useAgent()
  const { topFilter, inbox, conversations } = useAppSelector(selectConvo)
  const { setAccepted, handleSelect: handleConvoSelect } = useQueueState()
  const { isSmall, isMedium } = useBreakpoint()
  const [selected, setSelected] = React.useState<ChannelOptionsType>('Email')
  const [subject, setSubject] = React.useState('')
  const [users, setUsers] = React.useState([])
  const [files, setFiles] = React.useState([])
  const [creatingSms, setCreatingSms] = React.useState(false)
  const [creatingEmail, setCreatingEmail] = React.useState(false)
  const [value, setValue] = React.useState('')
  const [undo, setUndo] = React.useState({
    hasUndoReplacedString: false,
    isReplacedString: false,
    undoText: ''
  })

  const isSMS = selected === 'SMS'
  const ref = React.useRef(null)
  const inputContainerRef = React.useRef<HTMLDivElement>(null)
  const emailInputRef = React.useRef<HTMLInputElement>(null)
  const smsInputRef = React.useRef<HTMLInputElement>(null)
  const size = useSize(inputContainerRef) as any

  React.useEffect(() => {
    setUsers([])
    setSubject('')
    setFiles([])
    setCreatingSms(false)
    setCreatingEmail(false)
    if (selected === 'Email') {
      setTimeout(() => {
        emailInputRef?.current?.focus()
      }, 1)
    } else {
      setTimeout(() => {
        smsInputRef?.current?.focus()
      }, 1)
    }
  }, [selected])

  const handleInputContainerClick = React.useCallback(() => {
    ref?.current?.focus()
  }, [])

  const handleDeleteFile = React.useCallback(index => {
    setFiles(old => {
      const files = [...old]
      files.splice(index, 1)
      return files
    })
  }, [])

  const showError = React.useCallback(
    (message, timeout = undefined) => {
      dispatch(AddErrorAlert({ err: { message } }, timeout))
    },
    [dispatch]
  )

  const joinConversation = React.useCallback(
    async (session, joinConvo) => {
      const { id: sessionId } = session

      const initialSession =
        conversations[sessionId] || (await getSession(sessionId))
      if ([TOP_FILTERS.TRANSFERS, TOP_FILTERS.OFFLINE].includes(topFilter)) {
        dispatch(setTopFilter(TOP_FILTERS.ALL))
      }

      const newConversation = await formatConversation(initialSession, false)
      dispatch(
        setConversation({ id: sessionId, conversation: newConversation })
      )
      setAccepted(true)
      await joinSession(sessionId)
      if (joinConvo) {
        handleConvoSelect(sessionId)
      }
    },
    [
      dispatch,
      conversations,
      handleConvoSelect,
      setAccepted,
      formatConversation,
      joinSession,
      topFilter
    ]
  )

  // Creates the session
  const createNewSession = React.useCallback(
    async (user, channel, body, joinConvo = true) => {
      const me = currentUser()?.id
      // params are array of userIds, topic, self id and owner id
      const obj = await createSessionObjectV2(
        [user.id, me],
        !isSMS ? subject : null,
        me,
        user.id,
        inbox
      )

      try {
        // @ts-ignore
        const newSession = await createSession(obj)
        // @ts-ignore
        newSession.user = user
        const message = {
          content: body
        } as any
        if (isSMS) {
          message.number = channel
        } else {
          message.title = subject
          message.email = channel
        }

        joinConversation(newSession, joinConvo)
        if (joinConvo) {
          dispatch(switchSession(newSession.id))
          dispatch(changeType(isSMS ? TYPES.SMS : TYPES.EMAIL))
          dispatch(setChannelValue({ isPhone: isSMS, id: channel }))
        }
        messageHandlerV2(message, selected, files, newSession.id, false, false)

        onClose()
      } catch (err) {
        console.error(`Error creating session in ${selected} Modal`, err)
        dispatch(AddErrorAlert({ err }))
      }
    },
    [
      currentUser,
      dispatch,
      inbox,
      isSMS,
      onClose,
      selected,
      subject,
      files,
      messageHandlerV2,
      joinConversation
    ]
  )

  const handleSend = React.useCallback(() => {
    if (users.length === 0) {
      showError(`Please enter a valid ${isSMS ? 'phone number' : 'email'}`)
      return
    }

    if (!isSMS && !subject.length) {
      showError('Please enter a subject')
      return
    }

    if (creatingSms || creatingEmail) {
      showError(
        'New contact creation in progress, finish creating the new contact'
      )
      return
    }

    // const body = editorState.getCurrentContent().getPlainText()
    // if (!body.length && files.length === 0) {
    //   showError('Please enter a message body or attach a file')
    //   return
    // }

    const invalidChannels = users.filter(entry => !entry.channel.validated)
    if (invalidChannels.length > 0) {
      const errorMsg = invalidChannels.map(entry => {
        return entry.channel.value
      })

      showError(
        `${errorMsg.join(' , ')} ${
          errorMsg.length === 1
            ? 'is not a verified channel'
            : 'are not verified channels'
        } `,
        5000
      )
      return
    }

    //check if there are repetead users but on different channels, if yes, we only send it once.
    const filteredData = users.reduce((accumulated, current) => {
      const { user, channel } = current

      //dont add
      if (accumulated.some(entry => entry.user.id === user.id)) {
        return accumulated
      }
      //add
      accumulated.push({ user, channel })
      return accumulated
    }, [])

    if (filteredData.length === 1) {
      const { user, channel } = filteredData[0]
      createNewSession(user, channel.id, value, true)
    } else {
      //creating multiple sessions, so deselect conversation if any.
      handleConvoSelect('')
      //create sessions
      for (const detail of filteredData) {
        const { user, channel } = detail
        createNewSession(user, channel.id, value, false)
      }
    }
  }, [
    users,
    isSMS,
    subject.length,
    creatingSms,
    creatingEmail,
    showError,
    createNewSession,
    value,
    handleConvoSelect
  ])

  const formatCreateUser = React.useCallback(
    (value: string, name: string, method: ChannelOptionsType) => ({
      name,
      role_id: null,
      avatar_id: null,
      status_id: null,
      channels: [
        {
          type: method === 'SMS' ? 'sms_voice' : 'email',
          name: method,
          value
        }
      ],
      tags: []
    }),
    []
  )

  const setFile = React.useCallback((file: File) => {
    if (file) {
      setFiles(old => [...old, file])
    }
  }, [])

  const checkSubmit = React.useCallback(
    e => {
      const { keyCode } = e
      if (
        keyCode === UNDO_KEYCODE &&
        (e.ctrlKey || e.metaKey) &&
        undo.isReplacedString
      ) {
        setValue(undo.undoText)
        setUndo(old => ({ ...old, hasUndoReplacedString: true }))
      } else if (keyCode === SPACE_KEYCODE) {
        const { newText, originalText, replaced } = transformToEmoji(value)
        setUndo({
          hasUndoReplacedString: false,
          isReplacedString: replaced,
          undoText: originalText
        })
        setValue(newText)
      }
    },
    [undo.isReplacedString, undo.undoText, value]
  )

  const handleEmojiSelect = React.useCallback(emoji => {
    const val = emoji.native
    setValue(old => `${old} ${val} `)
  }, [])

  const disabled =
    selected === 'SMS'
      ? value.length > MAX_SMS_TEXT_LENGHT || users.length === 0
      : value.length === 0 || users.length === 0
  const classes = useStyles({
    maxHeight: Math.floor(size?.height)
  })

  return (
    <BaseModal
      open
      //@ts-ignore
      title={
        isMedium ? (
          <>
            <ArrowLeft className={classes.returnIcon} onClick={onClose} />
            Start New Conversation
          </>
        ) : (
          'Start New Conversation'
        )
      }
      onClose={onClose}
      showTitleOnMobile
      hideTitle={false}
      showX={!isMedium}
      PaperProps2={{
        style: {
          padding: '30px 16px 30px 16px',
          border: '1px solid #C6C6C6',
          borderRadius: '4px',
          width: '100%',
          maxWidth: isMedium ? 'unset' : '35%',
          height: isSmall && '100svh',
          maxHeight: isSmall && 'unset',
          minHeight: isSmall && 'auto'
        }
      }}
      classes={{ title: classes.modalTitle, close: classes.modalClose }}
      style={{ marginBottom: isMedium && 66 }}
    >
      <div className={classes.root}>
        <h2 className={clsx(classes.subtitle, classes.channelSubtitle)}>
          Channel
        </h2>
        <DropdownV2
          value={selected}
          options={CHANNEL_OPTIONS}
          onSelect={(val: ChannelOptionsType) => setSelected(val)}
        />
      </div>

      {selected === 'SMS' && (
        <div>
          <h2 className={clsx(classes.subtitle, classes.channelSubtitle)}>
            Contact
          </h2>
          <SmsInput
            users={users}
            setUsers={setUsers}
            type={'sms_voice'}
            ref={smsInputRef}
            createUser={formatCreateUser}
            creatingSms={creatingSms}
            setCreatingSms={setCreatingSms}
          />
        </div>
      )}

      <h2 className={clsx(classes.subtitle, classes.messageSubtitle)}>
        Message
      </h2>

      <div className={classes.messageRoot}>
        {selected === 'Email' && (
          <EmailInput
            subject={subject}
            setSubject={setSubject}
            users={users}
            setUsers={setUsers}
            type={'email'}
            ref={emailInputRef}
            createUser={formatCreateUser}
            creatingEmail={creatingEmail}
            setCreatingEmail={setCreatingEmail}
            isMedium={isMedium}
          />
        )}
        <div
          className={classes.inputRoot}
          ref={inputContainerRef}
          onClick={handleInputContainerClick}
        >
          <MentionInput
            value={value}
            placeholder={'Send a message ...'}
            onChange={e => setValue(e.target.value)}
            className={classes.mentionInputClass}
            autoFocus={false}
            inputClass='text'
            isP2P={false}
            focus={false}
            type={undefined}
            onKeyDown={checkSubmit}
            disableAgents
            inputProps={{ maxLength: 300 }}
          />
        </div>
        {files.length > 0 && (
          <div className={classes.holder}>
            <Attachments data={files} handleRemove={handleDeleteFile} />
          </div>
        )}

        {isSMS && (
          <div className={classes.count}>
            {value.length}/{MAX_SMS_TEXT_LENGHT}
          </div>
        )}

        <div className={classes.inputControlsRoot}>
          <div className={classes.iconButtonBlock}>
            <EmojiSelect handleEmojiSelect={handleEmojiSelect} disabled={false}>
              {props => (
                <Happyemoji
                  size='24'
                  color={'#8F8F8F'}
                  className={classes.iconButton}
                  {...props}
                />
              )}
            </EmojiSelect>

            <UploadFile
              onSetFile={setFile}
              fileLength={Boolean(files.length === 5)}
              disabled={files.length === 5}
              enablePastingFiles
              multi
              name=''
            >
              <AttachmentIcon fill={'#8F8F8F'} className={classes.iconButton} />
            </UploadFile>

            <MicrophoneIcon fill={'#8F8F8F'} className={classes.iconButton} />
          </div>

          <div className={classes.sendBlock}>
            <div className={classes.cannedButton}>
              <ThunderIcon />
            </div>

            <Button
              onClick={handleSend}
              className={classes.sendButton}
              disabled={disabled}
              endIcon={<SendIcon className='icon' />}
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </BaseModal>
  )
}

export default NewConversationModal
